import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { FaCheck, FaDiscord, FaLink } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthProvider";
import { useProfiles } from "../../../context/ProfilesProvider";
import {
  CANCEL_CB_TOKEN,
  GENERATE_CB_TOKEN,
} from "../../../queries/mutation/cb/cb_auth";
import { GET_MY_CB_LINK } from "../../../queries/mutation/cb/cb_queries";

// How often to look for the tip once a code is out.
const POLL_MS = 5000;
// Matches the server's code lifetime (CBLinkToken expires after 15 minutes).
const CODE_TTL_MINUTES = 15;
const RESEND_WAIT_MS = 15000;

type CbLink = { cbUsername: string; linkedAt: string | null };

const CBLinkWidget = () => {
  const { user } = useAuth();
  const { discordProfile, discordLoading, refetchCBProfile } = useProfiles();
  const { data, loading, startPolling, stopPolling } = useQuery(GET_MY_CB_LINK, {
    fetchPolicy: "network-only",
  });
  const link: CbLink | null = data?.getMyChaturbateLink ?? null;
  // Already linked and switching to a different account.
  const [changing, setChanging] = useState(false);
  // Set when a code is out: the CB name linked at that moment, so the tip
  // is known to have landed once the name differs. null name = first link.
  const [waiting, setWaiting] = useState<{ cbUsername: string | null } | null>(
    null,
  );
  const [canResend, setCanResend] = useState(true);

  // The link happens on Chaturbate's side (a 1-token tip), so this page
  // watches for it rather than being told.
  useEffect(() => {
    if (!waiting) {
      stopPolling();
      return;
    }
    startPolling(POLL_MS);
    return () => stopPolling();
  }, [waiting, startPolling, stopPolling]);

  useEffect(() => {
    if (!waiting || !link || link.cbUsername === waiting.cbUsername) {
      return;
    }
    toast.success(
      waiting.cbUsername
        ? `Chaturbate account changed to ${link.cbUsername}`
        : "Chaturbate Linked! :)",
    );
    setWaiting(null);
    setChanging(false);
    // Home, the nav and the tipper profile read the CB profile.
    refetchCBProfile();
  }, [link, waiting, refetchCBProfile]);

  const [generateToken, { loading: sending }] = useMutation(GENERATE_CB_TOKEN, {
    onError: (error) => {
      toast.error(error.message);
    },
    onCompleted: () => {
      toast.success("Code has been sent to your Discord DM");
      setWaiting({ cbUsername: link?.cbUsername ?? null });
      setCanResend(false);
      setTimeout(() => setCanResend(true), RESEND_WAIT_MS);
    },
  });

  // The code would stay good for 15 minutes otherwise, and a tip with it
  // after Cancel would switch the account with nobody watching.
  const [cancelToken] = useMutation(CANCEL_CB_TOKEN, {
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const cancel = () => {
    if (waiting) {
      cancelToken();
    }
    setWaiting(null);
    setChanging(false);
  };

  const broadcaster = user?.broadcaster ?? "the broadcaster";

  let body;
  if (discordLoading || (loading && !data)) {
    body = <div className="loading loading-spinner" />;
  } else if (!discordProfile) {
    body = (
      <div className="card-body">
        <div className="card-title flex flex-row justify-between">
          Link Your Chaturbate Account
        </div>
        <div className="flex flex-row justify-between">
          Please Link Your Discord Account first
          <div className="text-primary">
            <FaDiscord size={24} />
          </div>
        </div>
      </div>
    );
  } else if (link && !changing) {
    body = (
      <div className="card-body border-2 border-success rounded-box">
        <div className="flex flex-col justify-center items-center h-full gap-2">
          <div>Chaturbate Linked!</div>
          <div className="text-success">
            <FaCheck size={24} />
          </div>
          <div className="text-sm text-base-content/70 text-center break-all">
            <span className="text-warning">{link.cbUsername}</span>
            {link.linkedAt && (
              <div className="text-xs">
                since {new Date(link.linkedAt).toLocaleDateString()}
              </div>
            )}
          </div>
          <button
            className="btn btn-outline btn-sm mt-2"
            onClick={() => setChanging(true)}
          >
            Change Chaturbate account
          </button>
        </div>
      </div>
    );
  } else {
    // Link form - for a first link, or to switch to a different account.
    body = (
      <div className="card-body">
        <div className="card-title justify-between">
          <div>
            {changing ? "Change Chaturbate Account" : "Link Your Chaturbate Account"}
          </div>
          <div className="text-primary">
            <FaLink size={24} />
          </div>
        </div>
        <div className="divider divider-primary m-0" />
        {changing && link && (
          <div className="alert alert-info py-2 text-sm">
            <span>
              The new account replaces{" "}
              <span className="text-warning">{link.cbUsername}</span>. Fan club
              VIP follows the Chaturbate account. Your crimcoins, subscriptions
              and any VIP you were granted stay with your Reinstorm account.
            </span>
          </div>
        )}
        <ol className="list-decimal list-inside text-sm flex flex-col gap-1">
          <li>Press Send Code - the bot DMs you a 5-digit code.</li>
          <li>
            From the Chaturbate account you want linked, tip 1 token in{" "}
            {broadcaster}'s room with the code as the tip note.
          </li>
          <li>This page updates on its own once the tip lands.</li>
        </ol>
        {waiting && (
          <div className="text-sm text-center">
            <span className="loading loading-dots loading-xs align-middle mr-1" />
            Waiting for your 1-token tip. The code expires in{" "}
            {CODE_TTL_MINUTES} minutes.
            {!canResend && (
              <div className="text-xs text-base-content/60">
                You can request another code in a few seconds.
              </div>
            )}
          </div>
        )}
        <div className="card-actions justify-end gap-2">
          {(changing || waiting) && (
            <button className="btn btn-ghost" onClick={cancel}>
              Cancel
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={() => generateToken()}
            disabled={sending || !canResend}
          >
            {sending ? "Sending..." : waiting ? "Resend Code" : "Send Code"}
          </button>
        </div>
      </div>
    );
  }

  return <div className="card bg-base-100 w-full sm:w-96">{body}</div>;
};

export default CBLinkWidget;
